import React, { useState } from 'react';
import { Container, Card, Button, Alert, ProgressBar, ListGroup } from 'react-bootstrap';
import PortalHeader from '../../components/Portal/PortalHeader';
import { linkLessonsToStudents } from '../../utils/linkLessonsToStudents';

const LinkLessonsPage = () => {
	const [running, setRunning] = useState(false);
	const [progress, setProgress] = useState('');
	const [results, setResults] = useState(null);
	const [logs, setLogs] = useState([]);

	const handleLink = async () => {
		setRunning(true);
		setProgress('Starting...');
		setResults(null);
		setLogs([]);

		try {
			const result = await linkLessonsToStudents((message) => {
				setProgress(message);
				setLogs(prev => [...prev, message]);
			});

			setResults(result);
			setProgress('Complete!');
		} catch (error) {
			console.error('Error:', error);
			setProgress(`Error: ${error.message}`);
		} finally {
			setRunning(false);
		}
	};

	return (
		<>
			<PortalHeader
				title="Link Lessons to Students"
				subtitle="Automatically match imported lessons to student profiles"
			/>
			<Container className='py-4'>
				<Card className='shadow-sm mb-4'>
					<Card.Body>
						<h5 className='mb-3'>
							<i className='fa-sharp fa-solid fa-link me-2' style={{ color: '#384971' }}></i>
							Link Unlinked Lessons
						</h5>
						<p className='text-muted'>
							This tool will scan all lessons that have a student name but aren't linked to a student profile,
							and automatically match them based on name similarity.
						</p>
						<Button
							variant='primary'
							size='lg'
							onClick={handleLink}
							disabled={running}
						>
							{running ? (
								<>
									<i className='fa-sharp fa-solid fa-spinner fa-spin me-2'></i>
									Linking...
								</>
							) : (
								<>
									<i className='fa-sharp fa-solid fa-play me-2'></i>
									Start Linking
								</>
							)}
						</Button>
					</Card.Body>
				</Card>

				{progress && (
					<Card className='shadow-sm mb-4'>
						<Card.Header className='bg-transparent'>
							<h6 className='mb-0'>Progress</h6>
						</Card.Header>
						<Card.Body>
							<Alert variant='info' className='mb-0'>
								{progress}
							</Alert>
						</Card.Body>
					</Card>
				)}

				{logs.length > 0 && (
					<Card className='shadow-sm mb-4'>
						<Card.Header className='bg-transparent d-flex justify-content-between align-items-center'>
							<h6 className='mb-0'>Log ({logs.length} messages)</h6>
							<Button
								variant='outline-secondary'
								size='sm'
								onClick={() => setLogs([])}
							>
								Clear
							</Button>
						</Card.Header>
						<Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
							<pre style={{ fontSize: '0.85rem', marginBottom: 0 }}>
								{logs.join('\n')}
							</pre>
						</Card.Body>
					</Card>
				)}

				{results && (
					<Card className='shadow-sm'>
						<Card.Header className='bg-transparent'>
							<h6 className='mb-0'>Results Summary</h6>
						</Card.Header>
						<Card.Body>
							<div className='row g-3 mb-4'>
								<div className='col-md-4'>
									<Card className='border-0 bg-light text-center'>
										<Card.Body>
											<h2 className='mb-0' style={{ color: '#384971' }}>{results.total}</h2>
											<small className='text-muted'>Total Unlinked</small>
										</Card.Body>
									</Card>
								</div>
								<div className='col-md-4'>
									<Card className='border-0 bg-light text-center'>
										<Card.Body>
											<h2 className='mb-0' style={{ color: '#28a745' }}>{results.linked}</h2>
											<small className='text-muted'>Successfully Linked</small>
										</Card.Body>
									</Card>
								</div>
								<div className='col-md-4'>
									<Card className='border-0 bg-light text-center'>
										<Card.Body>
											<h2 className='mb-0' style={{ color: '#ffc107' }}>{results.notMatched}</h2>
											<small className='text-muted'>Not Matched</small>
										</Card.Body>
									</Card>
								</div>
							</div>

							{Object.keys(results.matchedStudents).length > 0 && (
								<>
									<h6 className='mt-4 mb-3'>
										<i className='fa-sharp fa-solid fa-check-circle me-2 text-success'></i>
										Matched Students ({Object.keys(results.matchedStudents).length})
									</h6>
									<ListGroup className='mb-4'>
										{Object.entries(results.matchedStudents).map(([lessonName, data]) => (
											<ListGroup.Item key={lessonName}>
												<div className='d-flex justify-content-between align-items-center'>
													<div>
														<strong>{lessonName}</strong>
														<i className='fa-sharp fa-solid fa-arrow-right mx-2 text-muted'></i>
														{data.studentName}
													</div>
													<span className='badge bg-primary'>{data.lessonCount} lessons</span>
												</div>
											</ListGroup.Item>
										))}
									</ListGroup>
								</>
							)}

							{results.unmatchedNames.length > 0 && (
								<>
									<h6 className='mt-4 mb-3'>
										<i className='fa-sharp fa-solid fa-exclamation-triangle me-2 text-warning'></i>
										Unmatched Names ({results.unmatchedNames.length})
									</h6>
									<Alert variant='warning'>
										<p className='mb-2'>
											These student names from lessons couldn't be matched to any student profile.
											You may need to:
										</p>
										<ul className='mb-0'>
											<li>Create missing student profiles</li>
											<li>Check for name spelling differences</li>
											<li>Manually link these lessons from the lesson management page</li>
										</ul>
									</Alert>
									<ListGroup>
										{results.unmatchedNames.map((item, index) => (
											<ListGroup.Item key={index}>
												<div className='d-flex justify-content-between align-items-center'>
													<span>{item.name}</span>
													<span className='badge bg-secondary'>{item.lessonCount} lessons</span>
												</div>
											</ListGroup.Item>
										))}
									</ListGroup>
								</>
							)}

							{results.errors.length > 0 && (
								<>
									<h6 className='mt-4 mb-3'>
										<i className='fa-sharp fa-solid fa-times-circle me-2 text-danger'></i>
										Errors ({results.errors.length})
									</h6>
									<Alert variant='danger'>
										<ListGroup variant='flush'>
											{results.errors.map((error, index) => (
												<ListGroup.Item key={index} className='bg-transparent'>
													<small>
														{error.general ? 'General Error' : `Lesson ${error.lessonId} (${error.studentName})`}
														: {error.error}
													</small>
												</ListGroup.Item>
											))}
										</ListGroup>
									</Alert>
								</>
							)}
						</Card.Body>
					</Card>
				)}
			</Container>
		</>
	);
};

export default LinkLessonsPage;
